import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IExperienceDenormalizedV2,
  ITaskDenormalized,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import { AllDone, InstanceTimelineGroup } from '../InstanceTimelineGroup';
import { InstanceWeek } from '../Nav/WeekNav';
import { OverdueSection } from '../OverdueSection';

export const InstanceMainWeekNav = ({
  instance,
  weekGroups,
  selectedWeek,
  currentWeek,
  actorAccessToken,
  instanceAccessToken,
  returnTo
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  weekGroups: InstanceWeek[];
  selectedWeek: number;
  currentWeek: number;
  n: moment.Moment;
  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
}) => {
  const selectedWeekGroup = useMemo(() => {
    return weekGroups.find((w) => w.count === selectedWeek);
  }, [weekGroups, selectedWeek]);

  const allComplete = useMemo(
    () =>
      instance.data.tasks.every((tsk) => tsk.status === TaskStatus.COMPLETE),
    [instance.data.tasks]
  );

  // Don't go through all overdue tasks, but only those which are overdue from
  // previous weeks. The ones of the current week are on the screen anyway
  // and there should be no future ones.
  const overdueTasks = useMemo(() => {
    if (selectedWeek !== currentWeek) {
      return EMPTY_ARR;
    }
    // Multi-week spanning tasks can appear in multiple weeks - kick them out
    const ots = uniqBy(
      weekGroups.reduce<ITaskDenormalized[]>((m, x) => {
        // We only show items from the past
        if (x.count < currentWeek) {
          return [
            ...m,
            ...x.tasks.filter((tsk) => tsk.status === TaskStatus.LATE)
          ];
        }
        return m;
      }, []),
      (tsk) => tsk.id
    );
    // Tasks can bleed over into multiple weeks when they have longer deadlines, so it
    // might be featured in the current week as well. Filter them out.
    return ots.filter(
      (tsk) => !selectedWeekGroup?.tasks.find((otsk) => otsk.id === tsk.id)
    );
  }, [weekGroups, selectedWeek, currentWeek, selectedWeekGroup]);
  const isFuture = !!selectedWeekGroup && selectedWeekGroup.count > currentWeek;
  return (
    <FlexContainerVertical fullWidth spacing={4}>
      {!!overdueTasks.length && (
        <OverdueSection
          overdueTasks={overdueTasks}
          actorAccessToken={actorAccessToken}
          instanceAccessToken={instanceAccessToken}
          returnTo={returnTo}
        />
      )}
      {selectedWeekGroup ? (
        <InstanceTimelineGroup
          instance={instance}
          weekGroup={selectedWeekGroup}
          actorAccessToken={actorAccessToken}
          instanceAccessToken={instanceAccessToken}
          isFuture={isFuture}
          returnTo={returnTo}
        />
      ) : allComplete ? (
        <AllDone />
      ) : null}
    </FlexContainerVertical>
  );
};
